<template>
  <RolesTable
    :rows="roles"
    :active="(row, _) => route?.params?.id?.toString() === row.id"
    :loading="isLoading || isDeletingRole"
    :page="currentPage"
    :searchEnabled="true"
    :searchQuery="searchQuery"
    :has-more-items="hasNextPage"
    :removable="true"
    :editable="true"
    :level="'tenant'"
    @click-row="redirectToRole"
    @load="fetchNextPage"
    @search="search($event)"
    @create="showCreateRoleModal = true"
    @remove="
      (row: Role) => {
        activeRole = row;
        showDeleteRoleModal = true;
      }
    "
    @edit="
      (row: Role) => {
        activeRole = row;
        showUpdateRoleModal = true;
      }
    "
    @promote="doPromoteRole"
    @demote="doDemoteRole"
    @set-ranking="doSetRanking"
    :loading-ranking="isDemotingRole || isPromotingRole || isSettingRanking"
  />

  <!-- CREATE -->
  <MutationRoleModal
    v-if="showCreateRoleModal"
    @close="showCreateRoleModal = false"
    @mutate="onCreateRole"
    :loading="isCreatingRole"
    :tenant-id="props.model.id"
  />

  <!-- UPDATE -->
  <MutationRoleModal
    v-if="showUpdateRoleModal && activeRole"
    :role="activeRole"
    :loading="isUpdatingRole"
    @close="closeUpdateModal"
    @mutate="onUpdateRole"
    :tenant-id="props.model.id"
  />

  <InfuseConfirmModal
    v-if="showDeleteRoleModal"
    :action-title="t('iam.roles.header.delete_role')"
    :action-description="t('iam.roles.info.delete_role', { role: activeRole?.name || '' })"
    verification-text="delete"
    @confirm="onDeleteRole"
    @close="closeDeleteModal"
  />
</template>

<script setup lang="ts">
import { Tenant } from '@infuse/api/iam/tenant';
import { CreateRole, Role, UpdateRole } from '@infuse/iam/role/type';
import {
  useCreateTenantRole,
  useDeleteTenantRole,
  useDemoteTenantRole,
  usePromoteTenantRole,
  useSetTenantRoleRanking,
  useUpdateTenantRole
} from '@infuse/iam/tenant/mutations';
import { useTenantRoles } from '@infuse/iam/tenant/queries';
import { MutationRoleModal, RolesTable } from '@infuse/iam/role/ui';
import { ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { InfuseConfirmModal } from '@infuse/ui/components';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps<{
  model: Tenant;
}>();

defineEmits(['reloadModel']);

const {
  fetchParentId: fetchTenantId,
  roles,
  currentPage,
  isLoading,
  hasNextPage,
  fetchNextPage,
  searchQuery
} = useTenantRoles();

const activeRole = ref<Role | undefined>(undefined);

const { mutateAsync: createRole, isPending: isCreatingRole } = useCreateTenantRole();
const { mutateAsync: updateRole, isPending: isUpdatingRole } = useUpdateTenantRole();
const { mutateAsync: deleteRole, isPending: isDeletingRole } = useDeleteTenantRole();
const { mutateAsync: promoteRole, isPending: isPromotingRole } = usePromoteTenantRole();
const { mutateAsync: demoteRole, isPending: isDemotingRole } = useDemoteTenantRole();
const { mutateAsync: setRanking, isPending: isSettingRanking } = useSetTenantRoleRanking();

const router = useRouter();
const route = useRoute();

const showCreateRoleModal = ref(false);
const showUpdateRoleModal = ref(false);
const showDeleteRoleModal = ref(false);

watch(
  route,
  () => {
    fetchTenantId.value = route.params.id as string;
  },
  { immediate: true }
);

watch(
  () => route.query.search,
  (search) => {
    searchQuery.value = search?.toString();
  },
  { immediate: true }
);

async function search(search: string): Promise<void> {
  searchQuery.value = search;
  replaceQueryParams(searchQuery.value);
}

function replaceQueryParams(searchQuery: string | undefined) {
  if (router.currentRoute.value.name !== 'tenant.roles') {
    // only do this if the user is currently on this page and not some sub-route.
    return;
  }

  router.replace({
    name: 'tenant.roles',
    query: {
      search: searchQuery || undefined
    }
  });
}

function redirectToRole(row: Role): void {
  router.push({
    name: 'tenant.role.permissions',
    params: { id: props.model.id, roleId: row.id }
  });
}

async function onCreateRole(vars: { role: CreateRole }): Promise<void> {
  await createRole({ parentId: props.model.id, role: vars.role });
  showCreateRoleModal.value = false;
}

async function onDeleteRole(): Promise<void> {
  if (!activeRole.value) {
    return;
  }

  await deleteRole({ parentId: props.model.id, roleId: activeRole.value.id });
  closeDeleteModal();
}

function closeDeleteModal() {
  showDeleteRoleModal.value = false;
  activeRole.value = undefined;
}

async function onUpdateRole(vars: { id?: string; role: UpdateRole | CreateRole }): Promise<void> {
  if (vars.id) {
    await updateRole({ parentId: props.model.id, roleId: vars.id, role: vars.role as UpdateRole });
    closeUpdateModal();
  }
}

function closeUpdateModal() {
  showUpdateRoleModal.value = false;
  activeRole.value = undefined;
}

async function doDemoteRole(role: Role): Promise<void> {
  await demoteRole({ parentId: props.model.id, roleId: role.id });
}

async function doPromoteRole(role: Role): Promise<void> {
  await promoteRole({ parentId: props.model.id, roleId: role.id });
}

async function doSetRanking(role: Role, ranking: number): Promise<void> {
  await setRanking({ parentId: props.model.id, roleId: role.id, ranking });
}
</script>
